import { Playlist, Song } from '@/types/schedule';
import { localStateStore } from '@/utils/localStateStore';

interface SpecialDayConfig {
  enabled: boolean;
  specialDayType: string;
  beforeEzanHours: number;
  afterEzanMinutes: number;
  playlist: Playlist;
  startDate?: string | null;
  endDate?: string | null;
  lastUpdated?: string;
}

interface SpecialDayRuntime {
  isActive: boolean;
  activeDateKey: string | null;
  windowStartIso: string | null;
  windowEndIso: string | null;
}

class SpecialDayService {
  private static instance: SpecialDayService;

  private config: SpecialDayConfig | null = null;
  private runtime: SpecialDayRuntime = {
    isActive: false,
    activeDateKey: null,
    windowStartIso: null,
    windowEndIso: null
  };

  private checkInterval: number | null = null;
  private readonly CHECK_INTERVAL_MS = 30 * 1000;
  private readonly STORAGE_CONFIG_KEY = 'specialDayConfig';
  private readonly STORAGE_RUNTIME_KEY = 'specialDayRuntime';

  private constructor() {
    this.loadConfig();
    this.loadRuntime();
    this.startChecker();
  }

  static getInstance(): SpecialDayService {
    if (!SpecialDayService.instance) {
      SpecialDayService.instance = new SpecialDayService();
    }
    return SpecialDayService.instance;
  }

  setSpecialDayConfig(rawConfig: any): boolean {
    try {
      const normalized = this.normalizeConfig(rawConfig);
      if (!normalized) {
        console.warn('[SpecialDayService] Geçersiz özel gün config verisi');
        return false;
      }

      this.config = {
        ...normalized,
        lastUpdated: new Date().toISOString()
      };

      this.saveConfig();

      window.dispatchEvent(new CustomEvent('special-day-playlist-updated', {
        detail: {
          action: 'set',
          config: this.config
        }
      }));

      this.runCheckNow();
      return true;
    } catch (error) {
      console.error('[SpecialDayService] Config kaydetme hatası:', error);
      return false;
    }
  }

  deleteSpecialDayConfig(): boolean {
    try {
      const wasActive = this.runtime.isActive;

      this.config = null;
      this.saveConfig();

      this.runtime = {
        isActive: false,
        activeDateKey: null,
        windowStartIso: null,
        windowEndIso: null
      };
      this.saveRuntime();

      if (wasActive) {
        window.dispatchEvent(new CustomEvent('special-day-playlist-ended', {
          detail: { reason: 'config-deleted' }
        }));
      }

      window.dispatchEvent(new CustomEvent('special-day-playlist-updated', {
        detail: {
          action: 'delete',
          config: null
        }
      }));

      return true;
    } catch (error) {
      console.error('[SpecialDayService] Config silme hatası:', error);
      return false;
    }
  }

  getSpecialDayConfig(): SpecialDayConfig | null {
    return this.config;
  }

  isActive(): boolean {
    return this.runtime.isActive;
  }

  runCheckNow(): void {
    this.checkSpecialDayWindow();
  }

  private loadConfig(): void {
    try {
      const raw = localStateStore.getString(this.STORAGE_CONFIG_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      const normalized = this.normalizeConfig(parsed);
      if (normalized) {
        this.config = normalized;
      } else {
        localStateStore.remove(this.STORAGE_CONFIG_KEY);
      }
    } catch (error) {
      console.error('[SpecialDayService] Config yükleme hatası:', error);
      localStateStore.remove(this.STORAGE_CONFIG_KEY);
    }
  }

  private saveConfig(): void {
    try {
      if (this.config) {
        localStateStore.setJson(this.STORAGE_CONFIG_KEY, this.config);
      } else {
        localStateStore.remove(this.STORAGE_CONFIG_KEY);
      }
    } catch (error) {
      console.error('[SpecialDayService] Config kaydetme hatası:', error);
    }
  }

  private loadRuntime(): void {
    try {
      const raw = localStateStore.getString(this.STORAGE_RUNTIME_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      this.runtime = {
        isActive: parsed?.isActive === true,
        activeDateKey: parsed?.activeDateKey || null,
        windowStartIso: parsed?.windowStartIso || null,
        windowEndIso: parsed?.windowEndIso || null
      };
    } catch (error) {
      console.error('[SpecialDayService] Runtime yükleme hatası:', error);
      localStateStore.remove(this.STORAGE_RUNTIME_KEY);
    }
  }

  private saveRuntime(): void {
    try {
      localStateStore.setJson(this.STORAGE_RUNTIME_KEY, this.runtime);
    } catch (error) {
      console.error('[SpecialDayService] Runtime kaydetme hatası:', error);
    }
  }

  private startChecker(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = window.setInterval(() => {
      this.checkSpecialDayWindow();
    }, this.CHECK_INTERVAL_MS);
  }

  private checkSpecialDayWindow(): void {
    if (!this.config || !this.config.enabled) {
      this.deactivateIfNeeded('disabled');
      return;
    }

    const now = new Date();
    if (!this.isSpecialDayDate(now, this.config)) {
      this.deactivateIfNeeded('out-of-date-range');
      return;
    }

    const maghribDate = this.getMaghribDate(now);
    if (!maghribDate) {
      this.deactivateIfNeeded('no-maghrib-time');
      return;
    }

    const startTime = new Date(maghribDate.getTime() - this.config.beforeEzanHours * 60 * 60 * 1000);
    const endTime = new Date(maghribDate.getTime() + this.config.afterEzanMinutes * 60 * 1000);
    const dayKey = this.toDateKey(now);
    const inWindow = now >= startTime && now <= endTime;

    if (inWindow) {
      if (!this.runtime.isActive || this.runtime.activeDateKey !== dayKey) {
        this.runtime = {
          isActive: true,
          activeDateKey: dayKey,
          windowStartIso: startTime.toISOString(),
          windowEndIso: endTime.toISOString()
        };
        this.saveRuntime();

        window.dispatchEvent(new CustomEvent('special-day-playlist-started', {
          detail: {
            playlist: this.config.playlist,
            specialDayType: this.config.specialDayType,
            beforeEzanHours: this.config.beforeEzanHours,
            afterEzanMinutes: this.config.afterEzanMinutes,
            windowStart: startTime.toISOString(),
            windowEnd: endTime.toISOString()
          }
        }));
      }
      return;
    }

    if (this.runtime.isActive) {
      this.runtime = {
        isActive: false,
        activeDateKey: null,
        windowStartIso: null,
        windowEndIso: null
      };
      this.saveRuntime();

      window.dispatchEvent(new CustomEvent('special-day-playlist-ended', {
        detail: {
          reason: 'window-ended',
          dayKey
        }
      }));
    }
  }

  private deactivateIfNeeded(reason: string): void {
    if (!this.runtime.isActive) return;

    this.runtime = {
      isActive: false,
      activeDateKey: null,
      windowStartIso: null,
      windowEndIso: null
    };
    this.saveRuntime();

    window.dispatchEvent(new CustomEvent('special-day-playlist-ended', {
      detail: { reason }
    }));
  }

  private getMaghribDate(now: Date): Date | null {
    try {
      const prayerTimesRaw = localStateStore.getString('prayerTimes');
      if (!prayerTimesRaw) return null;

      const prayerTimesData = JSON.parse(prayerTimesRaw);
      const prayerTimes = prayerTimesData?.prayerTimes;
      if (!prayerTimes) return null;

      let maghrib: string | null = null;

      if (Array.isArray(prayerTimes)) {
        const maghribEntry = prayerTimes.find((item: any) => {
          const normalizedName = String(item?.name || '').toLowerCase();
          return normalizedName.includes('akşam') || normalizedName.includes('aksam') || normalizedName.includes('maghrib');
        });
        maghrib = maghribEntry?.time || null;
      } else if (typeof prayerTimes === 'object') {
        maghrib = prayerTimes.maghrib || prayerTimes.Maghrib || prayerTimes.aksam || prayerTimes.akşam || null;
      }

      if (!maghrib || typeof maghrib !== 'string') return null;

      const timeMatch = maghrib.match(/(\d{1,2}):(\d{2})/);
      if (!timeMatch) return null;

      const hours = Number(timeMatch[1]);
      const minutes = Number(timeMatch[2]);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

      const maghribDate = new Date(now);
      maghribDate.setHours(hours, minutes, 0, 0);

      return maghribDate;
    } catch (error) {
      console.error('[SpecialDayService] Akşam vakti parse hatası:', error);
      return null;
    }
  }

  private isSpecialDayDate(now: Date, config: SpecialDayConfig): boolean {
    const currentKey = this.toDateKey(now);
    const startKey = config.startDate ? this.toDateKey(new Date(config.startDate)) : null;
    const endKey = config.endDate ? this.toDateKey(new Date(config.endDate)) : null;

    if (startKey && currentKey < startKey) return false;
    if (endKey && currentKey > endKey) return false;

    return true;
  }

  private toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private normalizeConfig(rawConfig: any): SpecialDayConfig | null {
    if (!rawConfig) return null;

    const playlist = this.normalizePlaylist(rawConfig.playlist);
    if (!playlist) return null;

    const beforeCandidates = [
      rawConfig.beforeEzanHours,
      rawConfig.beforeHours,
      rawConfig.startOffsetHours
    ];
    const afterCandidates = [
      rawConfig.afterEzanMinutes,
      rawConfig.afterMinutes,
      rawConfig.continueAfterEzanMinutes
    ];

    const beforeRaw = beforeCandidates.find((value) => value !== undefined && value !== null);
    const afterRaw = afterCandidates.find((value) => value !== undefined && value !== null);

    const beforeEzanHours = this.normalizeBeforeHours(beforeRaw);
    const afterEzanMinutes = this.normalizeAfterMinutes(afterRaw);

    return {
      enabled: rawConfig.enabled !== false,
      specialDayType: rawConfig.specialDayType || rawConfig.dayType || 'ramadan',
      beforeEzanHours,
      afterEzanMinutes,
      playlist,
      startDate: rawConfig.startDate || null,
      endDate: rawConfig.endDate || null,
      lastUpdated: rawConfig.lastUpdated
    };
  }

  private normalizeBeforeHours(value: any): number {
    const numeric = Number(value);
    if ([1, 2, 3].includes(numeric)) return numeric;
    return 2;
  }

  private normalizeAfterMinutes(value: any): number {
    const numeric = Number(value);
    if ([0, 30, 60].includes(numeric)) return numeric;
    return 30;
  }

  private normalizePlaylist(rawPlaylist: any): Playlist | null {
    if (!rawPlaylist || !Array.isArray(rawPlaylist.songs)) return null;

    const normalizedSongs: Song[] = rawPlaylist.songs
      .map((song: any, index: number) => {
        const filePath = song.filePath || song.path || song.url || song.cdnUrl;
        if (!filePath) return null;

        const title = song.title || song.name || `Şarkı ${index + 1}`;
        return {
          _id: song._id || song.id || `special_song_${index}_${Date.now()}`,
          title,
          name: song.name || title,
          artist: song.artist || 'Bilinmeyen Sanatçı',
          filePath,
          duration: Number(song.duration) || 0,
          cdnUrl: song.cdnUrl || song.url,
          artwork: song.artwork
        };
      })
      .filter((song: Song | null): song is Song => song !== null);

    if (normalizedSongs.length === 0) return null;

    return {
      _id: rawPlaylist._id || rawPlaylist.id || `special_day_playlist_${Date.now()}`,
      name: rawPlaylist.name || rawPlaylist.title || 'Özel Gün Playlist',
      songs: normalizedSongs,
      artwork: rawPlaylist.artwork || rawPlaylist.artworkUrl,
      baseUrl: rawPlaylist.baseUrl
    };
  }
}

export default SpecialDayService.getInstance();
